import { Search, X } from "lucide-react";
import {
  type InputHTMLAttributes,
  type ReactNode,
  forwardRef,
  useRef,
} from "react";
import { cn } from "../cn";

export interface SearchInputProps
  extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * Invoked by the trailing clear button. The button is only rendered when the
   * input has a value, so a caller that omits this while passing a `value`
   * still gets a functional field — it just won't offer a clear affordance.
   */
  readonly onClear?: () => void;
  /**
   * Slot rendered at the trailing edge while the field is empty (e.g. a
   * keyboard-shortcut hint). It is hidden once a value is present so it never
   * competes with the clear button for the same space.
   */
  readonly endAdornment?: ReactNode;
}

/**
 * Presentational search field: a leading search icon, the `type="search"`
 * input, and a trailing clear button that appears only when there is a value.
 * Like {@link Input} it is form-agnostic and forwards its ref to the real DOM
 * node so callers can focus it (e.g. a Cmd+K shortcut). The clear button
 * returns focus to the input so keyboard users are not stranded after wiping
 * the field.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ className, value, onClear, endAdornment, ...props }, forwardedRef) => {
    // A local ref is kept alongside the forwarded one so `onClear` can restore
    // focus even when the caller passes an object ref, a callback ref, or none
    // at all.
    const localRef = useRef<HTMLInputElement | null>(null);
    const hasValue = value !== undefined && value !== "";

    const setRefs = (node: HTMLInputElement | null): void => {
      localRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        // The forwarded object ref's `current` is typed readonly, but React
        // itself mutates it here; this is the standard ref-merging cast.
        (forwardedRef as { current: HTMLInputElement | null }).current = node;
      }
    };

    return (
      <div className="relative w-full">
        <Search
          className="pointer-events-none absolute inset-y-0 left-3 my-auto size-4 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          ref={setRefs}
          type="search"
          value={value}
          className={cn(
            "h-10 w-full rounded-xl border border-input bg-surface pl-10 pr-10 text-sm text-foreground shadow-sm transition-colors placeholder:text-muted-foreground hover:bg-muted/50 focus-visible:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/15 disabled:pointer-events-none disabled:opacity-50 aria-[invalid=true]:border-destructive [&::-webkit-search-cancel-button]:hidden",
            className,
          )}
          {...props}
        />
        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
          {hasValue ? (
            <button
              type="button"
              aria-label="Wissen"
              onClick={() => {
                onClear?.();
                localRef.current?.focus();
              }}
              className="pointer-events-auto grid size-5 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-3.5" aria-hidden="true" />
            </button>
          ) : (
            endAdornment
          )}
        </div>
      </div>
    );
  },
);
SearchInput.displayName = "SearchInput";
